import * as React from "react"
import { type IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import { SharedBarChartStyles } from "src/frontend/components/OS/Shared/Charts/styles"
import { ChartContainer } from "src/frontend/components/Shared/Layout/Components/Charts/shared"
import { type XAxisProps } from "src/frontend/components/Shared/Layout/Components/Charts/TimeseriesChartAxis"
import {
  type MetricSeries,
  type MetricValue,
} from "src/frontend/components/Shared/Layout/Components/Charts/toMetricSeries"
import {
  MetricComponentDateSummary,
  MetricComponentSummary,
} from "src/frontend/components/Shared/Layout/Components/Headers/MetricComponentSummary"
import { MetricSeriesBarChartTile } from "src/frontend/components/Shared/Layout/Components/Tiles/MetricSeriesBarChartTile"
import { type ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import { type SizingProps } from "src/frontend/components/Shared/Layout/types"
import { ParentSizedMetricSeriesBarChart } from "./MetricSeriesBarChart"

/*
  INTERFACES
*/

interface MetricBarChartComponentProps extends SizingProps {
  componentSize: ComponentSize
  title: React.ReactNode | undefined
  icon: React.ReactNode | undefined
  metricSeries: MetricSeries
  origin: IntervalOrigin
  loading: boolean
  skipAnimations: boolean
  onMouseOver?: (value: MetricValue, index: number) => void
  onMouseOut?: (value?: MetricValue) => void
  onClick?: (value: MetricValue, index: number) => void
  selectedIndex?: number
  xAxisIntervalFormat?: XAxisProps["intervalFormatFn"]
  noDelta?: boolean
}

/*
  COMPONENTS
*/

export const MetricBarChartComponent = React.memo<MetricBarChartComponentProps>(
  ({
    componentSize,
    title,
    icon,
    metricSeries,
    origin,
    loading,
    height,
    skipAnimations,
    onMouseOver,
    onMouseOut,
    onClick,
    selectedIndex,
    xAxisIntervalFormat,
    noDelta,
  }) => {
    const preselected =
      typeof selectedIndex === "number" ? metricSeries.values[selectedIndex] : undefined
    if (componentSize.isSmall) {
      return (
        <>
          <MetricComponentDateSummary
            componentSize={componentSize}
            title={title}
            icon={icon}
            metricSeries={metricSeries}
            interval={origin.interval}
            selectedIndex={selectedIndex}
            noDelta={noDelta}
          />
          <MetricSeriesBarChartTile
            metricSeries={metricSeries}
            interval={origin.interval}
            loading={loading}
            skipAnimations={skipAnimations}
            selectedIndex={selectedIndex}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            onClick={onClick}
            height={height}
            chartStyle={SharedBarChartStyles}
            xAxisIntervalFormat={xAxisIntervalFormat}
          />
        </>
      )
    }

    return (
      <>
        <MetricComponentSummary
          componentSize={componentSize}
          title={title}
          icon={icon}
          metricSeries={metricSeries}
          interval={origin.interval}
          selectedIndex={selectedIndex}
          noDelta={noDelta}
        />
        <ChartContainer height={height} width="auto">
          <ParentSizedMetricSeriesBarChart
            metricSeries={metricSeries.values}
            interval={origin.interval}
            loading={loading}
            skipAnimations={skipAnimations}
            preselectedValue={preselected}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            onClick={onClick}
            chartStyle={SharedBarChartStyles}
            xAxisIntervalFormat={xAxisIntervalFormat}
          />
        </ChartContainer>
      </>
    )
  }
)
